<template>
  <WizardContent
    :show-sidebar="false"
    class="wizard-wrapper"
  >
    <template #flashMessage>
      <EduWarningFlashMessage :email="email" />
    </template>
    <BackButton
      v-show="showFullForm"
      class="mr-auto"
      @click="goBack"
    />
    <GateHeader>
      <template #logo>
        <JobNavIcon />
      </template>
      <template #page-title>
        Welcome to JobNav!
      </template>
      <template #below-title>
        Already have an account?
        <router-link
          to="login"
        >
          Sign in
        </router-link>
      </template>
    </GateHeader>
    <LandingAuth
      v-if="!emailChecked && !showFullForm"
      v-model="email"
      :trim-email="trimEmail"
      :loading="false"
      @submit-check="checkLoginMethod"
    />
    <div v-else class="w-100">
      <v-form
        ref="form"
        v-model="valid"
        class="w-100"
        @submit.prevent="submitForm"
      >
        <div
          v-if="!singleSignOnMode"
          class="flex-grow-1 d-flex align-center justify-space-between"
        >
          <DpplSignupFormHalfTextField
            v-model="firstName"
            label="First Name"
            class="form-text-field"
            :rules="firstNameRules"
            validate-on="blur"
            has-details
          />
          <DpplSignupFormHalfTextField
            v-model="lastName"
            label="Last Name"
            class="form-text-field"
            :rules="lastNameRules"
            validate-on="blur"
            has-details
            margin="0px 0px 0px 24px"
          />
        </div>
        <DpplTextField
          v-model="email"
          label="Email"
          readonly
          class="form-text-field"
          has-details
          @change="trimEmail"
        />
        <DpplTextField
          v-if="!singleSignOnMode"
          v-model="password"
          class="form-text-field mb-1"
          label="Password"
          :rules="passwordRules"
          type="password"
          autocomplete="off"
          validate-on="blur"
          data-sentry-mask
        />
        <div class="w-100 text-right">
          <router-link
            class="text-button-2 text-linkDefault text-decoration-underline"
            to="password-reset"
          >
            Reset your password
          </router-link>
        </div>
        <DpplSignupFormButton
          class="mb-4 mt-4"
          type="submit"
          block
          :loading="loading"
        >
          Continue
        </DpplSignupFormButton>
      </v-form>
      <section
        aria-label="Oauth and SSO logins"
        class="signup-btns"
      >
        <GoogleButton
          :google-url="googleUrl"
        />
        <MicrosoftButton
          :microsoft-url="microsoftUrl"
        />
        <SsoLoginButton
          v-if="ssoName || isGoogleSso || isMicrosoftSso"
          class="mb-6 w-100"
          :is-active="singleSignOnMode"
          :sso-provider="ssoProviderName"
          :sso-name="ssoName"
          :is-google="isGoogleSso"
          :is-microsoft="isMicrosoftSso"
          @toggle-sso="singleSignOnMode = !singleSignOnMode"
        />
      </section>
    </div>
    <p class="text-body-3 text-textDark pt-6">
      <a
        href="https://hello.datapeople.io/docs/tos"
        target="_blank"
        rel="noopener noreferrer"
        class="text-decoration-underline text-linkDefault"
      >
        Terms of Service
      </a>
      &nbsp;and
      <a
        href="https://www.payscale.com/about/privacy-policy"
        target="_blank"
        rel="noopener noreferrer"
        class="text-decoration-underline text-linkDefault"
      >
        Privacy Policy
      </a>
    </p>
  </WizardContent>
</template>

<script>
import { mapActions } from 'pinia';
import WizardContent from '@/components/shared/WizardContent.vue';
import SsoLoginButton from '@/components/signup/SsoLoginButton.vue';
import EduWarningFlashMessage from '@/components/shared/EduWarningFlashMessage.vue';
import DpplSignupFormHalfTextField from '@/components/datapeople/DpplSignupFormHalfTextField.vue';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplSignupFormButton from '@/components/datapeople/DpplSignupFormButton.vue';
import GateHeader from '@/components/shared/gates/GateHeader.vue';
import LandingAuth from '@/components/shared/gates/auth/LandingAuth.vue';
import BackButton from '@/components/shared/gates/auth/BackButton.vue';
import GoogleButton from '@/components/shared/gates/auth/GoogleButton.vue';
import MicrosoftButton from '@/components/shared/gates/auth/MicrosoftButton.vue';
import {
  signupBuildAuthUrls,
  signupCreateAccount,
  signupLoginMethod,
} from '@/api/signup';
import { redirectToSsoProvider } from '@/helpers/redirect';
import validation from '@/helpers/validation';
import { useWizard } from '@/stores/wizard/wizard';
import JobNavIcon from '@/assets/jobnav-payscale-icon-logo.svg?component';

export default {
  name: 'SignupView',
  components: {
    WizardContent,
    DpplSignupFormHalfTextField,
    DpplTextField,
    DpplSignupFormButton,
    EduWarningFlashMessage,
    SsoLoginButton,
    GateHeader,
    JobNavIcon,
    LandingAuth,
    BackButton,
    GoogleButton,
    MicrosoftButton,
  },
  emits: ['track-page-load'],
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      emailChecked: false,
      firstNameRules: validation.firstNameRules(),
      lastNameRules: validation.lastNameRules(),
      emailRules: validation.emailRules(),
      passwordRules: validation.passwordRules(),
      valid: false,
      loading: false,
      googleUrl: null,
      microsoftUrl: null,
      singleSignOnMode: false,
      showFullForm: false,
      ssoProviderName: '',
      ssoName: '',
      isGoogleSso: false,
      isMicrosoftSso: false,
    };
  },
  head() {
    return {
      title: this.$route.meta.title || 'JobNav',
      link: [
        { rel: 'canonical', href: 'https://app.datapeople.io/' },
      ],
      meta: [
        {
          name: 'description',
          content: 'Create an account to begin writing job descriptions that are thoughtful, concise, and welcoming to candidates from every background.',
        },
        {
          name: 'robots',
          content: 'max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        },
        {
          name: 'og:locale',
          content: 'en_US',
        },
        {
          name: 'og:type',
          content: 'website',
        },
        {
          name: 'og:title',
          content: 'Create an Account - JobNav',
        },
        {
          name: 'og:description',
          content: 'Create an account to begin writing job descriptions that are thoughtful, concise, and welcoming to candidates from every background.',
        },
        {
          name: 'og:url',
          content: 'https://app.datapeople.io/',
        },
        {
          name: 'og:site_name',
          content: 'JobNav',
        },
        {
          name: 'og:image',
          content: 'https://static.tapwage.com/taprecruit/jobnav-og-1200x630.jpg',
        },
        {
          name: 'og:image:secure_url',
          content: 'https://static.tapwage.com/taprecruit/jobnav-og-1200x630.jpg',
        },
        {
          name: 'og:image:width',
          content: '1200',
        },
        {
          name: 'og:image:height',
          content: '630',
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:description',
          content: 'Create an account to begin writing job descriptions that are thoughtful, concise, and welcoming to candidates from every background.',
        },
        {
          name: 'twitter:title',
          content: 'Create an Account - JobNav',
        },
        {
          name: 'twitter:site',
          content: '@Datapeopleio',
        },
        {
          name: 'twitter:image',
          content: 'https://static.tapwage.com/taprecruit/jobnav-og-1200x630.jpg',
        },
        {
          name: 'twitter:creator',
          content: '@Datapeopleio',
        },
      ],
    };
  },
  async created() {
    const urlFlashMessage = this.$route.query?.flash_message || '';
    const urlFlashColor = this.$route.query?.flash_color;
    if (urlFlashMessage || urlFlashColor) {
      this.updateFlash({ message: urlFlashMessage, status: urlFlashColor });
    }

    const getLinkedinAndGoogleOauthUrls = async () => {
      const responseJSON = await signupBuildAuthUrls();
      if (responseJSON.success === true) {
        this.googleUrl = responseJSON.google_auth_url;
        this.microsoftUrl = responseJSON.microsoft_auth_url;
      }
    };

    Promise.all([getLinkedinAndGoogleOauthUrls()]).catch((e) => {
      this.updateFlash({ message: e.message });
      this.loading = false;
    });
  },
  beforeCreate() {
    this.$emit('track-page-load', { page: 'Sign Up' });
  },
  methods: {
    ...mapActions(useWizard, ['updateFlash']),
    async checkLoginMethod() {
      if (!this.email) return;

      try {
        const result = await signupLoginMethod({ email: this.email });
        if (result?.success) {
          if (result.ssoMandatory) {
            const { from = '' } = this.$route.query;
            redirectToSsoProvider({
              from,
              provider: result.ssoName,
              isGoogle: result.loginMethod === 'google',
              isMicrosoft: result.loginMethod === 'microsoft',
            });
            return;
          }
          this.emailChecked = true;
          this.showFullForm = true;
          this.ssoProviderName = result.companyName;
          this.ssoName = result.ssoName;
          this.isGoogleSso = result.loginMethod === 'google';
          this.isMicrosoftSso = result.loginMethod === 'microsoft';
        }
      } catch (err) {
        this.updateFlash({ message: `Login method error: ${err.message}` });
        throw err;
      }
    },
    async submitForm() {
      try {
        const formValidateResult = await this.$refs.form.validate();
        if (formValidateResult.valid) {
          this.loading = true;
          const responseJSON = await signupCreateAccount({
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            password: this.password,
          });
          if (responseJSON.success === true) {
            this.$router.push({
              name: 'verify-email',
            });
          }
        }
      } catch (e) {
        if (!e.message || e.message.charAt(0) !== '{') {
          this.updateFlash({ message: `Create account error: ${e.message}` });
          throw e;
        }
        const responseJSON = JSON.parse(e.message);
        const { errors } = responseJSON;
        if (errors && errors.sso_mandatory) {
          const { from = '' } = this.$route.query;
          redirectToSsoProvider({
            from,
            provider: errors.provider,
            isGoogle: errors.is_google,
            isMicrosoft: errors.is_microsoft,
          });
          return;
        }
        // if response contains please use corporate email error and no errors in other fields
        if ( // If only error is please include corporate email
          responseJSON.errors
          && Object.keys(responseJSON.errors).length === 1
          && responseJSON.errors.email
          && responseJSON.errors.email.includes('Please use your corporate email address.')
        ) {
          this.$router.push({
            name: 'provide-work-email',
            params: {
              firstName: this.firstName,
              lastName: this.lastName,
              password: this.password,
            },
          });
        } else if (
          responseJSON.errors
          && responseJSON.errors.email
          && responseJSON.errors.email
            .includes('Please sign up with your individual work email address.')
        ) {
          this.updateFlash({ message: 'Please sign up with your individual work email address.' });
          return;
        } else if (
          // if response contains please use corporate email error and no errors in other fields
          responseJSON.errors
          && responseJSON.errors.auth
          && responseJSON.errors.auth.includes('user already exists.')
        ) {
          this.updateFlash({ template: 'user-exists' });
          return;
        } else {
          this.updateFlash({ message: 'Something went wrong' });
        }
        throw e;
      } finally {
        this.loading = false;
      }
    },
    trimEmail() {
      this.email = validation.trimSpaces(this.email);
    },
    goBack() {
      this.emailChecked = false;
      this.showFullForm = false;
    },
  },
};
</script>

<style scoped lang="less">
.wizard-wrapper {
  min-width: 330px;
  width: 100%;
  align-items: center;
  justify-content: center;
}

:deep(.wizard-flash-message) {
  border-top-right-radius: 0px !important;
}

:deep(.slot-flash-message.full-width) {
  width: 450px;
}

.signup-btns {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  gap: 16px;
}

.form-text-field :deep(.v-field .v-field__outline__start),
.form-text-field :deep(.v-field .v-field__outline__end) {
  border: 1px solid @grey-400;
}
</style>
